import React from "react";
import { Calendar, ArrowRightLong } from "@/components/icons";
import Chip from "@/components/ui/chip";
import FileImage from "@/components/ui/file-image";
import type { ClientAnnouncement } from "../../types/client-announcements.types";
import styles from "./AnnouncementCard.module.scss";

export interface AnnouncementCardProps {
  announcement: ClientAnnouncement;
  onClick?: () => void;
  className?: string;
}

const CATEGORY_CHIP_TYPE: Record<
  ClientAnnouncement["categoryType"],
  "success" | "primary" | "info" | "default"
> = {
  education: "success",
  system: "primary",
};

const AnnouncementCard: React.FC<AnnouncementCardProps> = ({
  announcement,
  onClick,
  className = "",
}) => {
  const chipType = CATEGORY_CHIP_TYPE[announcement.categoryType];
  const hasImage = Boolean(announcement.imageFileID || announcement.imageUrl);

  return (
    <article
      className={`${styles.card} ${hasImage ? styles.cardWithImage : ""} ${className}`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {hasImage && (
        <div className={styles.card__imageWrap}>
          {announcement.imageFileID ? (
            <FileImage
              fileId={announcement.imageFileID}
              alt=""
              className={styles.card__image}
              objectFit="cover"
            />
          ) : (
            <img
              src={announcement.imageUrl!}
              alt=""
              className={styles.card__image}
            />
          )}
          {onClick && (
            <div className={styles.card__imageHover}>
              <div className={styles.card__imageHoverArrowBg}>
                <ArrowRightLong width={24} height={24} color="#fff" />
              </div>
            </div>
          )}
        </div>
      )}
      <div className={styles.card__body}>
        <div className={styles.card__meta}>
          <span className={styles.card__date}>
            <Calendar
              width={16}
              height={16}
              className={styles.card__dateIcon}
            />
            {announcement.date}
          </span>
          <Chip type={chipType} variant="soft" size="small">
            {announcement.category}
          </Chip>
        </div>
        <h3 className={styles.card__title}>{announcement.title}</h3>
        <p className={styles.card__description}>{announcement.description}</p>
      </div>
    </article>
  );
};

export default AnnouncementCard;
